import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import {
  Activity,
  BatteryCharging,
  Bike,
  Clock,
  Download,
  FileSpreadsheet,
  Gauge,
  MapPin,
  Navigation,
  PackageCheck,
  Printer,
  RefreshCw,
  Route as RouteIcon,
  Search,
  Star,
  Timer,
  TriangleAlert,
  Truck,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  KpiCard,
  Panel,
  exportCsv,
  exportExcel,
  printPage,
  num,
  type ExportRow,
} from "@/components/admin/analytics/AnalyticsKit";
import { DemoMap } from "@/components/admin/tracking/DemoMap";
import { DriverPanel } from "@/components/admin/tracking/DriverPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeliveryTracking } from "@/hooks/useDeliveryTracking";
import {
  DRIVER_STATE_LABEL,
  type TrackedDriver,
  type TrackingSnapshot,
} from "@/lib/admin/tracking/provider";
import { istToday } from "@/lib/admin/production.schemas";

export const Route = createFileRoute("/_admin/admin/delivery-tracking")({
  head: () => ({
    meta: [
      { title: "Delivery Tracking — Admin" },
      {
        name: "description",
        content: "Live map, driver telemetry and delivery timelines for every active order.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DeliveryTrackingPage,
});

const STATE_BADGE: Record<string, string> = {
  available: "var(--ops-success)",
  delivering: "var(--ops-info)",
  returning: "var(--ops-warn)",
  delayed: "var(--ops-danger)",
};

function DeliveryTrackingPage() {
  const snapshot = useDeliveryTracking();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [day, setDay] = useState(istToday());
  const [driverFilter, setDriverFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [heatmap, setHeatmap] = useState(true);
  const [auto, setAuto] = useState(true);

  // Auto refresh off = freeze the last snapshot instead of following the feed.
  const frozen = useRef<TrackingSnapshot | null>(null);
  if (auto && snapshot) frozen.current = snapshot;
  const view = auto ? snapshot : frozen.current;

  const loading = view === null;
  const drivers = view?.drivers ?? [];

  const areas = useMemo(
    () => Array.from(new Set(drivers.map((d) => d.area))).sort(),
    [drivers],
  );
  const vehicles = useMemo(
    () => Array.from(new Set(drivers.map((d) => d.vehicleType))).sort(),
    [drivers],
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return drivers.filter((d) => {
      if (driverFilter && d.id !== driverFilter) return false;
      if (areaFilter && d.area !== areaFilter) return false;
      if (vehicleFilter && d.vehicleType !== vehicleFilter) return false;
      if (statusFilter && d.state !== statusFilter) return false;
      if (
        q &&
        ![d.name, d.vehicleLabel, d.area, d.delivery?.orderRef, d.delivery?.customer.name]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
        return false;
      return true;
    });
  }, [drivers, driverFilter, areaFilter, vehicleFilter, statusFilter, query]);

  const selected = drivers.find((d) => d.id === selectedId) ?? null;
  const k = view?.kpis;

  const exportRows = (): ExportRow[] =>
    rows.map((d) => ({
      Driver: d.name,
      Vehicle: d.vehicleLabel,
      Area: d.area,
      Order: d.delivery?.orderRef ?? "—",
      Customer: d.delivery?.customer.name ?? "—",
      Mobile: d.delivery?.customer.mobile ?? "—",
      Status: DRIVER_STATE_LABEL[d.state],
      "ETA (min)": d.delivery?.etaMinutes ?? 0,
      "Distance remaining (km)": d.delivery?.distanceRemainingKm ?? 0,
      "Speed (km/h)": d.fix.speed,
      "Battery %": d.fix.battery,
      "GPS updated": new Date(d.fix.timestamp).toLocaleString("en-IN"),
    }));

  return (
    <div>
      <AdminPageHeader
        title="Delivery Tracking"
        description="Track every active delivery in real time."
      />

      {/* toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 p-2 print:hidden">
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => setSelectedId((v) => v)}
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Refresh
        </Button>
        <Input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value || istToday())}
          className="h-9 w-40"
          aria-label="Tracking date"
        />
        <select
          aria-label="Driver filter"
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All drivers</option>
          {drivers.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Area filter"
          value={areaFilter}
          onChange={(e) => setAreaFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All areas</option>
          {areas.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          aria-label="Vehicle filter"
          value={vehicleFilter}
          onChange={(e) => setVehicleFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All vehicles</option>
          {vehicles.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <select
          aria-label="Status filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All statuses</option>
          {Object.entries(DRIVER_STATE_LABEL).map(([k2, label]) => (
            <option key={k2} value={k2}>
              {label}
            </option>
          ))}
        </select>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search driver, order, customer"
            aria-label="Search deliveries"
            className="h-9 w-56 pl-8"
          />
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => exportCsv("delivery-tracking", exportRows())}
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Export CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => exportExcel("delivery-tracking", exportRows())}
          >
            <FileSpreadsheet className="h-3.5 w-3.5" aria-hidden />
            Export Excel
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={printPage}>
            <Printer className="h-3.5 w-3.5" aria-hidden />
            Print
          </Button>
          <Switch id="tracking-auto" checked={auto} onCheckedChange={setAuto} />
          <Label htmlFor="tracking-auto" className="text-xs text-muted-foreground">
            Auto refresh 30s
          </Label>
          <span className="text-xs text-muted-foreground">
            {view
              ? `${view.mode === "demo" ? "Demo GPS" : "Live GPS"} · updated ${new Date(
                  view.generatedAt,
                ).toLocaleTimeString("en-IN")}`
              : "Connecting…"}
          </span>
        </div>
      </div>

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-8">
        <KpiCard label="Drivers on road" value={num(k?.driversOnRoad ?? 18)} tone="info" icon={Bike} loading={loading} />
        <KpiCard label="Out for delivery" value={num(k?.outForDelivery ?? 52)} tone="purple" icon={Truck} loading={loading} />
        <KpiCard label="Delivered today" value={num(k?.deliveredToday ?? 148)} tone="success" icon={PackageCheck} loading={loading} />
        <KpiCard label="Avg delivery time" value={`${k?.avgDeliveryMinutes ?? 31} mins`} tone="teal" icon={Timer} loading={loading} />
        <KpiCard label="Delayed deliveries" value={num(k?.delayedDeliveries ?? 6)} tone="danger" icon={TriangleAlert} loading={loading} />
        <KpiCard label="Idle drivers" value={num(k?.idleDrivers ?? 2)} tone="warn" icon={Clock} loading={loading} />
        <KpiCard label="Total distance today" value={`${num(k?.distanceTodayKm ?? 428)} km`} tone="neutral" icon={RouteIcon} loading={loading} />
        <KpiCard label="Customer rating" value={`${k?.customerRating ?? 4.8}★`} tone="success" icon={Star} loading={loading} />
      </div>

      {/* map + side panel */}
      <div className="mb-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Switch id="heatmap" checked={heatmap} onCheckedChange={setHeatmap} />
            <Label htmlFor="heatmap" className="text-xs text-muted-foreground">
              Delivery density heatmap
            </Label>
          </div>
          {loading ? (
            <Skeleton className="h-[520px] w-full" />
          ) : (
            <DemoMap
              drivers={rows}
              zones={view!.zones}
              selectedId={selectedId}
              onSelect={setSelectedId}
              showHeatmap={heatmap}
            />
          )}
        </div>
        <DriverPanel driver={selected} />
      </div>

      {/* zone summary */}
      <Panel title="Zone summary" subtitle="Mumbai delivery zones" className="mb-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
          {(view?.zones ?? []).map((z) => (
            <button
              key={z.zone}
              type="button"
              onClick={() => setAreaFilter(areaFilter === z.zone ? "" : z.zone)}
              className={`rounded-lg border p-3 text-left transition-colors ${
                areaFilter === z.zone ? "border-primary bg-muted" : "border-border hover:bg-muted/50"
              }`}
            >
              <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <MapPin className="h-3 w-3" aria-hidden />
                {z.zone}
              </p>
              <p className="mt-1 text-xl font-semibold tabular-nums">{z.deliveries}</p>
              <p className="text-[11px] text-muted-foreground">
                {z.avgMinutes} min avg · {z.driversActive} drivers
              </p>
            </button>
          ))}
        </div>
      </Panel>

      {/* live delivery table */}
      <Panel title="Live deliveries" subtitle={`${rows.length} drivers tracked`}>
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            No drivers match this selection.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[1100px] text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Driver</th>
                  <th className="px-3 py-2 text-left">Vehicle</th>
                  <th className="px-3 py-2 text-left">Area</th>
                  <th className="px-3 py-2 text-left">Current order</th>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">Mobile</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">ETA</th>
                  <th className="px-3 py-2 text-right">Distance left</th>
                  <th className="px-3 py-2 text-right">Speed</th>
                  <th className="px-3 py-2 text-right">Battery</th>
                  <th className="px-3 py-2 text-left">GPS updated</th>
                  <th className="px-3 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d: TrackedDriver) => (
                  <tr
                    key={d.id}
                    className={`border-t border-border ${selectedId === d.id ? "bg-muted/40" : ""}`}
                  >
                    <td className="px-3 py-2">
                      <span className="flex items-center gap-2">
                        <img
                          src={d.photoUrl}
                          alt=""
                          className="h-6 w-6 rounded-full object-cover"
                          loading="lazy"
                        />
                        {d.name}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{d.vehicleLabel}</td>
                    <td className="px-3 py-2">{d.area}</td>
                    <td className="px-3 py-2 font-mono text-xs">{d.delivery?.orderRef ?? "—"}</td>
                    <td className="px-3 py-2">{d.delivery?.customer.name ?? "—"}</td>
                    <td className="px-3 py-2 text-xs">{d.delivery?.customer.mobile ?? "—"}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: STATE_BADGE[d.state] }}
                        />
                        {DRIVER_STATE_LABEL[d.state]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {d.delivery ? `${d.delivery.etaMinutes} min` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {d.delivery ? `${d.delivery.distanceRemainingKm} km` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className="inline-flex items-center gap-1">
                        <Gauge className="h-3 w-3 text-muted-foreground" aria-hidden />
                        {d.fix.speed}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className="inline-flex items-center gap-1">
                        <BatteryCharging className="h-3 w-3 text-muted-foreground" aria-hidden />
                        {d.fix.battery}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(d.fix.timestamp).toLocaleTimeString("en-IN")}
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => setSelectedId(d.id)}
                      >
                        <Navigation className="h-3.5 w-3.5" aria-hidden />
                        View route
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Activity className="h-3.5 w-3.5" aria-hidden />
        {view?.providerName ?? "DemoGPSProvider"} — simulated GPS. Configure a Google Maps key or
        Mapbox token and a live driver feed to switch this page to live tracking with no UI change.
      </p>
    </div>
  );
}
